import React from "react";
import { Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { COLORS } from "../../assets/theme";
import CustomTable from "../../components/table/CustomTable";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import Actions from "../../components/Actions";
import GenericFormDialog, { Field } from "../../components/GenericFormDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomLabel from "../../components/CustomLabel";
import { useLocation, useParams } from "react-router-dom";
import { getAllBranches } from "../../services/BranchesService";
import {
  createCurrency,
  deleteCurrency,
  getAllCurrencys,
  updateCurrency,
} from "../../services/CurrencyService";

const Currencies: React.FC = () => {
  const location = useLocation();
  const params = useParams();
  const venueId = params.branchId;

  console.log("Reports component - params:", params);
  console.log("Reports component - venueId from params:", venueId);

  const [branches, setBranches] = React.useState<any[]>([]);
  const [selectValue, setSelectValue] = React.useState<
    string | number | (string | number)[]
  >("");
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<any | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState<string | number | null>(
    null
  );

  const [search, setSearch] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [refetchTrigger, setRefetchTrigger] = React.useState(0);

  const formFields: Field[] = [
    { name: "label", label: "Label" },
    { name: "value", label: "Value" },
  ];

  const openEditForId = (id: string | number) => {
    const found = rows.find((r: any) => String(r.id) === String(id));
    if (found) {
      setEditingRow(found);
      setEditOpen(true);
    }
  };
  const wiredActions = Actions({
    handleOpenEditModal: { handler: openEditForId },
    onDelete: {
      handler: async (id: number | string) => {
        setToDeleteId(id);
        setConfirmOpen(true);
      },
    },
  });

  const [rows, setRows] = React.useState<any[]>([]);
  React.useEffect(() => {
    const fetchBranches = async () => {
      try {
        if (!venueId) {
          setBranches([]);
          setSelectValue("");
          return;
        }

        const response = await getAllBranches(venueId);
        let branchesData = [];
        if (response && response.businesses) {
          branchesData = response.businesses;
        } else if (response && response.data) {
          branchesData = response.data;
        } else if (Array.isArray(response)) {
          branchesData = response;
        } else {
          console.warn("Unexpected branches response structure:", response);
        }

        const branchesArray = Array.isArray(branchesData) ? branchesData : [];
        setBranches(branchesArray);
        
        // Automatically select the first branch if available
        if (branchesArray.length > 0 && !selectValue) {
          setSelectValue(branchesArray[0].business_id);
        }
      } catch (error) {
        console.error("Error fetching branches:", error);
        setBranches([]);
        setSelectValue("");
      }
    };

    if (venueId) {
      fetchBranches();
    } else {
      console.log("No venueId available, skipping branch fetch");
      setBranches([]);
      setSelectValue("");
    }
  }, [venueId]);

  React.useEffect(() => {
    const fetchSpecialities = async () => {
      setLoading(true);
      try {
        const businessId = selectValue ? String(selectValue) : venueId;
        const response = await getAllCurrencys(businessId);
        console.log("Currencies fetched:", response);

        let specialitiesData: any[] = [];
        if (response && Array.isArray(response)) {
          specialitiesData = response;
        } else if (response && response.data) {
          specialitiesData = Array.isArray(response.data)
            ? response.data
            : response.data.Currencys || [];
        } else if (response && response.currencies) {
          specialitiesData = response.currencies;
        } else if (response && response.Currencys) {
          specialitiesData = response.Currencys;
        }

        const mapped = (specialitiesData || []).map((s: any) => ({
          id: s.id ?? s._id ?? s.currency_id,
          label: s.currency_label ?? s.label ?? s.name ?? "",
          value: s.currency_value ?? s.value ?? s.symbol ?? "",
        }));

        setRows(mapped);
      } catch (error: any) {
        console.error("Error fetching Currencys:", error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSpecialities();
  }, [refetchTrigger, selectValue, venueId]);

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <CustomLabel
          text="Currencies"
          variant="h4"
          fontWeight="semi-bold"
          sx={{ mb: 2 }}
        />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          selectConfig={{
            options: branches.map((branch) => ({
              label: branch.name || "Unknown Branch",
              value: branch.business_id,
            })),
            hidden: true,
            value: selectValue,
            onChange: (v: any) => {
              setSelectValue(v);
            },
            placeholder: "Select branch",
            size: "medium",
            searchable: false,
            clearable: true,
            multiSelect: false,
            sx: {
              backgroundColor: COLORS.WhiteBackground,
            },
          }}
        />
        <CustomTable<any>
          filters={{ page: 0, size: 10, search }}
          clientSidePagination={true}
          loading={loading}
          fields={[
            { key: "label", header: "Label" },
            { key: "value", header: "Value" },
          ]}
          fetchFunc={async (params?: any) => {
            const q = (params && params.search) || search || "";

            const normalizedQuery = String(q || "")
              .toLowerCase()
              .trim();

            const filtered = rows.filter((r: any) => {
              if (!normalizedQuery) return true;

              const haystack = [r.label, r.value]
                .filter(Boolean)
                .map((v: any) => String(v).toLowerCase())
                .join(" ");

              return haystack.includes(normalizedQuery);
            });

            const content = filtered;
            const totalElements = content.length;
            const pageSize = (params && params.size) || 10;
            const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

            return {
              content,
              number: 0,
              size: pageSize,
              totalElements,
              totalPages,
            };
          }}
        />
        <GenericFormDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title={editingRow ? `Edit Currency` : "Add Currency"}
          fields={editingRow ? formFields : [...formFields]}
          initialValues={
            editingRow
              ? {
                  label: editingRow.label || "",
                  value: editingRow.value || "",
                }
              : {}
          }
          onSubmit={async (values) => {
            try {
              const businessId = selectValue ? String(selectValue) : venueId;

              if (editingRow) {
                await updateCurrency(String(editingRow.id), {
                  label: values.label,
                  value: values.value,
                  business_id: businessId,
                });
                toast.success("Currency updated successfully!", {
                  position: "top-right",
                });
                setRefetchTrigger((prev) => prev + 1);
              } else {
                await createCurrency({
                  label: values.label,
                  value: values.value,
                  business_id: businessId,
                });
                toast.success("Currency created successfully!", {
                  position: "top-right",
                });
                setRefetchTrigger((prev) => prev + 1);
              }
              setEditOpen(false);
              setEditingRow(null);
            } catch (error: any) {
              console.error("Error saving Currency:", error);
              toast.error("Failed to save Currency. Please try again.", {
                position: "top-right",
              });
            }
          }}
        />
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setToDeleteId(null);
          }}
          title="Delete entry"
          message="Are you sure you want to delete this entry? This action cannot be undone."
          onConfirm={async () => {
            if (toDeleteId === null) return;
            try {
              await deleteCurrency(String(toDeleteId));
              toast.success("Currency deleted successfully!", {
                position: "top-right",
              });
              setRefetchTrigger((prev) => prev + 1);
            } catch (error: any) {
              console.error("Error deleting currency:", error);
              toast.error("Failed to delete currency. Please try again.", {
                position: "top-right",
              });
            } finally {
              setConfirmOpen(false);
              setToDeleteId(null);
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default Currencies;
